import React, { useEffect, useRef, useState } from "react";
import { listContactMessages, markContactMessageHandled } from "../../services/api/adminService";
import { isAuthError, isCanceledError } from "../../services/errors/errorHandler";
import { formatBusinessDateTime } from "../../services/utils/dateUtils";
import AdminHeader from "./AdminHeader";
import "./admin.css";

/** Filters: handled=false / handled=true / everything. */
export const MESSAGE_FILTERS = [
  { id: "todo", label: "To do", handled: false },
  { id: "handled", label: "Handled", handled: true },
  { id: "all", label: "All", handled: undefined },
];

const EMPTY_MESSAGES = {
  todo: "No messages to deal with.",
  handled: "No handled messages yet.",
  all: "No messages yet.",
};

const PAGE_SIZE = 50;

function MessageRow({ message, busy, onHandled }) {
  const handled = Boolean(message.handled_at);
  return (
    <li className="admin-booking" data-message-id={message.id}>
      <div className="admin-booking__head">
        <h2 className="admin-booking__time">{message.name || "—"}</h2>
        <div className="admin-badges">
          <span
            className={`admin-badge ${handled ? "admin-badge--status-completed" : "admin-badge--status-confirmed"}`}
            data-testid="message-status"
          >
            {handled ? "Handled" : "To do"}
          </span>
        </div>
      </div>

      <dl className="admin-fields">
        <div><dt>Received</dt><dd>{formatBusinessDateTime(message.inserted_date) || "—"}</dd></div>
        <div>
          <dt>Email</dt>
          <dd>{message.email ? <a href={`mailto:${message.email}`}>{message.email}</a> : "—"}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{message.phone ? <a href={`tel:${message.phone}`}>{message.phone}</a> : "—"}</dd>
        </div>
        {handled && (
          <div><dt>Handled</dt><dd>{formatBusinessDateTime(message.handled_at)}</dd></div>
        )}
      </dl>

      <p className="admin-message__text">{message.message}</p>

      {!handled && (
        <div className="admin-actions">
          <button type="button" className="admin-btn" disabled={busy} onClick={() => onHandled(message)}>
            Mark handled
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * Owner view of contact-form messages (GET /api/admin/contact-messages),
 * newest first, with "Mark handled" (POST .../<id>/handled).
 */
export default function AdminMessages() {
  const [filter, setFilter] = useState("todo");
  const [reloadKey, setReloadKey] = useState(0);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [unhandled, setUnhandled] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const handledRef = useRef(undefined);

  useEffect(() => {
    const controller = new AbortController();
    const { handled } = MESSAGE_FILTERS.find((f) => f.id === filter);
    handledRef.current = handled;
    setLoading(true);
    setError("");
    setMessages([]);
    setTotal(0);

    listContactMessages({ handled, limit: PAGE_SIZE, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setMessages(data.messages);
        setTotal(data.total);
        setUnhandled(data.unhandled);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        if (!isAuthError(err)) setError(err.message || "Could not load messages.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [filter, reloadKey]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await listContactMessages({
        handled: handledRef.current,
        limit: PAGE_SIZE,
        offset: messages.length,
      });
      const seen = new Set(messages.map((m) => m.id));
      setMessages([...messages, ...data.messages.filter((m) => !seen.has(m.id))]);
      setTotal(data.total);
      setUnhandled(data.unhandled);
    } catch (err) {
      if (!isAuthError(err)) setActionError(err.message || "Could not load more messages.");
    } finally {
      setLoadingMore(false);
    }
  };

  const markHandled = async (message) => {
    if (busyId !== null) return;
    setBusyId(message.id);
    setNotice("");
    setActionError("");
    try {
      const updated = await markContactMessageHandled(message.id);
      if (filter === "todo") {
        setMessages((prev) => prev.filter((m) => m.id !== message.id));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id && updated && typeof updated === "object" ? { ...m, ...updated } : m))
        );
      }
      if (!message.handled_at) setUnhandled((n) => (typeof n === "number" ? Math.max(0, n - 1) : n));
      setNotice("Message marked handled.");
    } catch (err) {
      if (!isAuthError(err)) setActionError(err.message || "That didn't work. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin">
      <AdminHeader messageCount={unhandled ?? null} />

      <main id="main" className="admin-main" tabIndex={-1}>
        <div className="admin-title-row">
          <h1>Messages</h1>
          <button
            type="button"
            className="admin-btn"
            onClick={() => { setNotice(""); setActionError(""); setReloadKey((k) => k + 1); }}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
        <p className="admin-muted">
          Messages sent through the contact form. Reply by email or phone, then mark them handled.
          {typeof unhandled === "number" && ` ${unhandled} to do.`}
        </p>

        <div className="admin-filters" role="group" aria-label="Show messages">
          {MESSAGE_FILTERS.map((f) => (
            <button
              key={f.id}
              id={`messages-filter-${f.id}`}
              type="button"
              aria-pressed={filter === f.id}
              className={`admin-tab ${filter === f.id ? "is-active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div aria-live="polite" className="admin-live">
          {notice && <p className="admin-notice">{notice}</p>}
        </div>
        {actionError && <p className="admin-error" role="alert">{actionError}</p>}

        <section aria-label="Contact messages" aria-busy={loading}>
          {loading && <p className="admin-muted" role="status">Loading messages…</p>}

          {!loading && error && (
            <div className="admin-error" role="alert">
              <p>{error}</p>
              <button type="button" className="admin-btn" onClick={() => setReloadKey((k) => k + 1)}>
                Try again
              </button>
            </div>
          )}

          {!loading && !error && messages.length === 0 && (
            <p className="admin-empty">{EMPTY_MESSAGES[filter]}</p>
          )}

          {!loading && !error && messages.length > 0 && (
            <>
              <p className="admin-muted admin-count">Showing {messages.length} of {total}</p>
              <ul className="admin-list">
                {messages.map((m) => (
                  <MessageRow key={m.id} message={m} busy={busyId !== null} onHandled={markHandled} />
                ))}
              </ul>
              {messages.length < total && (
                <button type="button" className="admin-btn" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
